/**
 * Modal for creating and editing actions
 */

import { useCallback, useEffect, useId, useRef, useState, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from '@/components/ui/toast';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { usePostHog } from 'src/lib/posthog';
import { startOfMonth, addMonths, format } from 'date-fns';
import {
  PaperclipIcon,
  SpinnerGapIcon,
  XIcon,
  Icon,
  CheckCircleIcon,
  ArchiveIcon,
  ArrowsLeftRightIcon,
  ArrowCounterClockwiseIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
  CaretDownIcon
} from '@phosphor-icons/react';
import { useHubUpload } from 'src/features/uploads/useHubUpload';
import Checkbox from '@/components/ui/Checkbox';
import { InputField } from '@/components/ui/Input';
import { DateInput } from '@/components/ui/DateInput';
import Modal from '@/components/ui/Modal';
import Dropdown from '@/components/ui/Dropdown';
import { Textarea } from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import Loading from '@/components/ui/Loading';
import { getActionDisplayStatus } from '../actionStatus';
import { ACTION_PRIORITY_OPTIONS, actionPriorityLabel } from '../actionPriority';
import { formatSuggestedPriority } from '../extractionPriority';
import ActionStatusPill from './ActionStatusPill';
import ActionPriorityPill from './ActionPriorityPill';
import { useQuery } from '@tanstack/react-query';
import { getTagCatalog } from '../api';
import { Action, ActionCreate, ActionPriority, ActionUpdate, Group, OrganizationUser } from '../types';
import { canCreateGroupAction } from '../permissions';
import ActionTagPills from './ActionTagPills';
import TagPillSelect from './TagPillSelect';
import { extractNameFromEmail } from 'src/components/ui/Combobox';
import { ResourceLink } from 'src/features/chat/components/ResourceLink';
import { ActionAssigneeMultiSelector } from './ActionAssigneeMultiSelector';
import { ActionCostSection } from './ActionCostSection';
import * as api from '../api';
import { ActionExtractionSourceResponse } from '../api';
import ActionSourceTooltip from './ActionSourceTooltip';
import { validateCost } from 'src/features/pam/costFormatting';
import { formatLongDate, formatShortDate } from 'src/lib/utils/formatDate';
import { getDocumentLinkByExternalId } from 'src/features/write/shared/api';
import { useRefreshAssociatedUsersOnOpen } from '../hooks';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import ImageModal from 'src/features/chat/components/ImageModal';
import UserAvatar from '@/components/ui/UserAvatar';
import { snakeToTitleCase } from 'src/lib/utils/formatLabel';

interface ActionMenuItem {
  label: string;
  icon?: Icon;
  iconProps?: {
    weight?: 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  };
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ActionCreate | ActionUpdate) => Promise<void>;
  action?: Action | null;
  users: OrganizationUser[];
  currentUserId?: number | null;
  isLoading?: boolean;
  /** Whether the user can edit core action fields (description, due date, assignee) */
  isEditable?: boolean;
  /** Whether the user can add progress updates (defaults to isEditable value) */
  canAddUpdates?: boolean;
  initialGroupId?: string | null;
  /**
   * Groups the user can attach a new action to. When provided on create (and
   * `initialGroupId` is not set), the Group field is a Personal / group picker.
   */
  groups?: Group[];
  /**
   * Safety-group domain slug. On create, the matching catalogue tag is
   * pre-selected so the user can see the tag the server will apply.
   */
  initialDomainSlug?: string | null;
  /** On create, pre-select the National Returns PAM tag (server also applies it). */
  preselectPamTag?: boolean;
  createGroupLabel?: string;
  /** Prefilled description when creating an action from another record. */
  initialDescription?: string;
  /** Prefilled priority when creating an action from another record. */
  initialPriority?: ActionPriority | '';
  /** Group name for an existing action, when the caller has that context. */
  groupLabel?: string | null;
  /** Group resource ID for an existing action, when the caller has that context. */
  groupResourceId?: string | null;
  groupCreationHint?: string;
  /** When false, hides the read-only Group row on the create form (e.g. PAM costed actions). Defaults to true. */
  showGroupOnCreate?: boolean;
  /**
   * When true (the default), shows the Cost section with a revenue/capital split
   * and a derived Total cost. When false, the legacy flat Cost input is saved as
   * the revenue component so it remains compatible with the split-only API.
   */
  showCostSection?: boolean;
  /**
   * Requests an AI cost estimate for the action being edited. When provided (and
   * the cost section is shown), the "Get AI cost estimate" affordance appears and
   * calls this; it resolves to the suggested revenue/capital split plus the
   * rationale. Only PAM actions supply this (AI estimation is PAM-only); omit it
   * elsewhere (and on create flows) to hide the affordance.
   */
  onEstimateCost?: () => Promise<{ revenue: number; capital: number; rationale: string }>;
  /** When set, shows a toggle to record edits as part of this meeting's audit trail */
  meetingId?: string | null;
  meetingTitle?: string | null;
  // Callbacks for action menu items
  /** Resolve using the comment box contents (text and/or attached files). */
  onResolve?: (action: Action, payload: { documentIds: string[]; text?: string }) => void | Promise<void>;
  onUnresolve?: (action: Action, payload: { documentIds: string[]; text?: string }) => void | Promise<void>;
  onArchive?: (action: Action) => void;
  onUnarchive?: (action: Action) => void;
  /** When provided, shows a "Move to another group" menu item for group actions. */
  onMove?: (action: Action) => void;
}

/** Normalised edit-form values, used to decide whether there is anything to save. */
interface FormSnapshot {
  description: string;
  dueDate: string;
  priority: string;
  assignees: string;
  cost: string;
  revenueCost: string;
  capitalCost: string;
  tagIds: string;
}

const PRIORITY_DROPDOWN_OPTIONS = [{ value: '', label: 'Not set' }, ...ACTION_PRIORITY_OPTIONS];

const buildFormSnapshot = (values: {
  description: string;
  dueDate: string;
  priority: string;
  assigneeEmails: string[];
  cost: string;
  revenueCost: string;
  capitalCost: string;
  tagIds: string[];
}): FormSnapshot => ({
  description: values.description.trim(),
  dueDate: values.dueDate || '',
  priority: values.priority || '',
  assignees: [...values.assigneeEmails].sort().join(','),
  cost: values.cost.trim(),
  revenueCost: values.revenueCost.trim(),
  capitalCost: values.capitalCost.trim(),
  tagIds: [...values.tagIds].sort().join(',')
});

const DESCRIPTION_URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const TRAILING_URL_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?']);

/** Drop sentence punctuation, and a closing ) or ] only when the URL did not open it. */
function splitTrailingUrlPunctuation(url: string): { href: string; trailing: string } {
  const schemeEnd = url.indexOf('://');
  const minEnd = schemeEnd === -1 ? url.length : schemeEnd + 3;
  let end = url.length;

  while (end > minEnd) {
    const char = url[end - 1];
    if (TRAILING_URL_PUNCTUATION.has(char)) {
      end -= 1;
      continue;
    }
    if (char === ')' || char === ']') {
      const opener = char === ')' ? '(' : '[';
      let depth = 0;
      for (let i = 0; i < end; i += 1) {
        if (url[i] === opener) depth += 1;
        else if (url[i] === char) depth -= 1;
      }
      if (depth < 0) {
        end -= 1;
        continue;
      }
    }
    break;
  }

  return { href: url.slice(0, end), trailing: url.slice(end) };
}

function DescriptionText({ text }: { text: string }) {
  const parts = text.split(DESCRIPTION_URL_PATTERN);
  return (
    <>
      {parts.flatMap((part, index) => {
        if (!/^https?:\/\//.test(part)) {
          return <span key={`${index}-text`}>{part}</span>;
        }
        const { href, trailing } = splitTrailingUrlPunctuation(part);
        const link = (
          <a
            key={`${index}-${href}`}
            href={href}
            className="text-misc-link break-all hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {href}
          </a>
        );
        return trailing ? [link, trailing] : link;
      })}
    </>
  );
}

export function ActionModal({
  isOpen,
  onClose,
  onSave,
  action,
  users,
  currentUserId,
  isLoading,
  isEditable = true,
  canAddUpdates,
  initialGroupId,
  groups,
  initialDomainSlug,
  preselectPamTag = false,
  initialDescription,
  initialPriority,
  createGroupLabel,
  groupLabel,
  groupResourceId,
  groupCreationHint,
  showGroupOnCreate = true,
  showCostSection = true,
  onEstimateCost,
  meetingId,
  meetingTitle,
  onResolve,
  onUnresolve,
  onArchive,
  onUnarchive,
  onMove
}: ActionModalProps) {
  // Default canAddUpdates to isEditable if not provided
  const canAddProgressUpdates = canAddUpdates ?? isEditable;
  const posthog = usePostHog();
  const descriptionFieldId = useId();
  const dueDateFieldId = useId();
  const priorityFieldId = useId();
  const assigneeFieldId = useId();
  const updateFieldId = useId();
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<string>('');
  const [priority, setPriority] = useState<ActionPriority | ''>('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const { data: tagCatalog = [] } = useQuery({
    queryKey: ['tag-catalog'],
    queryFn: getTagCatalog,
    enabled: isOpen
  });
  const [assigneeEmails, setAssigneeEmails] = useState<string[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [cost, setCost] = useState<string>('');
  const [revenueCost, setRevenueCost] = useState<string>('');
  const [capitalCost, setCapitalCost] = useState<string>('');
  const [isEstimatingCost, setIsEstimatingCost] = useState(false);
  const [costEstimate, setCostEstimate] = useState<{ revenue: number; capital: number; rationale: string } | null>(
    null
  );
  const [keptCostEstimateComments, setKeptCostEstimateComments] = useState<
    Array<{ revenue: number; capital: number; rationale: string }>
  >([]);
  const costFieldId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [extractionSource, setExtractionSource] = useState<ActionExtractionSourceResponse | null>(null);
  const [isLoadingExtractionSource, setIsLoadingExtractionSource] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [updateMode, setUpdateMode] = useState<'comment' | 'resolve' | 'reopen' | null>(null);
  const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const updateHub = useHubUpload({ documentType: 'action_evidence', skipDI: () => true });
  const hasInflightUploads = Array.from(updateHub.uploads.values()).some(
    (u) => u.stage === 'uploading' || u.stage === 'scanning' || u.stage === 'processing'
  );
  const [includeInMeeting, setIncludeInMeeting] = useState(!!meetingId);
  const [imagePreview, setImagePreview] = useState<{ url: string; filename: string; documentId: string } | null>(null);
  const [descriptionDiff, setDescriptionDiff] = useState<{
    author: string;
    oldValue: string;
    newValue: string;
  } | null>(null);
  const [isEditing, setIsEditing] = useState(!action);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(false);
  const [activityFilter, setActivityFilter] = useState<'all' | 'status' | 'comments'>('all');
  const descriptionPreviewRef = useRef<HTMLDivElement>(null);
  // Snapshot of the last-saved form values. The "Save Changes" button is only
  // enabled when the current values differ from this, so once a save lands —
  // including the immediate save behind "Keep" on an AI estimate — the button
  // goes quiet instead of implying there's still unsaved work.
  const [savedSnapshot, setSavedSnapshot] = useState<FormSnapshot>(() =>
    buildFormSnapshot({
      description: '',
      dueDate: '',
      priority: '',
      assigneeEmails: [],
      cost: '',
      revenueCost: '',
      capitalCost: '',
      tagIds: []
    })
  );
  const tenantNavigate = useTenantNavigate();
  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);

  const isEditMode = !!action;
  const canEditFields = isEditable && (!isEditMode || isEditing);
  const isActionInGroup = action
    ? action.short_reference !== 'PERSONAL' && action.short_reference !== 'PAM'
    : !!groupId;
  const canSelectGroupOnCreate = !isEditMode && showGroupOnCreate && !initialGroupId && groups !== undefined;
  const createGroupOptions = useMemo(() => {
    if (!groups) return [];
    return groups
      .filter((group) => canCreateGroupAction(group))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({ value: group.id, label: group.name }));
  }, [groups]);
  const effectiveGroupId = isEditMode ? (isActionInGroup ? initialGroupId : null) : groupId;
  const displayedGroupLabel = groupLabel ?? (initialGroupId ? (createGroupLabel ?? 'Group') : null);
  const displayedGroupResourceId = groupResourceId ?? initialGroupId ?? null;
  const groupDisplayLabel = isEditMode
    ? displayedGroupLabel
    : (createGroupLabel ?? (initialGroupId ? 'Group' : 'Personal'));
  const groupDisplayResourceId = isEditMode ? displayedGroupResourceId : (initialGroupId ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const assigneeBackfilledRef = useRef(false);

  const currentSnapshot = useMemo(
    () =>
      buildFormSnapshot({
        description,
        dueDate,
        priority,
        assigneeEmails,
        cost,
        revenueCost,
        capitalCost,
        tagIds
      }),
    [description, dueDate, priority, assigneeEmails, cost, revenueCost, capitalCost, tagIds]
  );
  // In edit mode, only offer "Save Changes" when something actually changed.
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(currentSnapshot) !== JSON.stringify(savedSnapshot),
    [currentSnapshot, savedSnapshot]
  );
  const handleCancelEdit = () => {
    setDescription(savedSnapshot.description);
    setDueDate(savedSnapshot.dueDate);
    setPriority((savedSnapshot.priority as ActionPriority) || '');
    setAssigneeEmails(savedSnapshot.assignees ? savedSnapshot.assignees.split(',') : []);
    setCost(savedSnapshot.cost);
    setRevenueCost(savedSnapshot.revenueCost);
    setCapitalCost(savedSnapshot.capitalCost);
    setTagIds(savedSnapshot.tagIds ? savedSnapshot.tagIds.split(',') : []);
    setErrors({});
    setIsEstimatingCost(false);
    setCostEstimate(null);
    setKeptCostEstimateComments([]);
    setIsEditing(false);
    setIsEditingDescription(false);
  };
  const chronologicalAuditLogs = useMemo(
    () => [...auditLogs].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [auditLogs]
  );
  const filteredAuditLogs = useMemo(
    () =>
      chronologicalAuditLogs.filter((log) => {
        if (activityFilter === 'comments') return !!log.text_update;
        if (activityFilter === 'status') {
          return !!log.old_value && !!log.new_value && log.old_value.status !== log.new_value.status;
        }
        return true;
      }),
    [activityFilter, chronologicalAuditLogs]
  );

  // Permission helpers — driven by onResolve/onArchive/onUnarchive props from parent,
  // these just gate whether the button is shown in the menu.
  const canResolve = !!action && action.status !== 'resolved' && action.status !== 'archived';
  const canUnresolve = !!action && action.status === 'resolved';
  const canArchive = !!action && action.status !== 'resolved' && action.status !== 'archived';
  const canUnarchive = !!action && action.status === 'archived';

  // Check if action is archived (cannot add updates to archived actions)
  const isArchived = action?.status === 'archived';

  // Refresh associated users on each open so assignee options reflect recent permission/assignment changes.
  const handleAssigneeSelectorOpen = useRefreshAssociatedUsersOnOpen({
    groupId: effectiveGroupId ?? null,
    actionId: action?.id
  });

  // Build action menu items
  const actionMenuItems = useMemo<ActionMenuItem[]>(() => {
    if (!action) return [];

    const items: ActionMenuItem[] = [];

    // ArchiveIcon/Unarchive
    if (canArchive && onArchive) {
      items.push({
        label: 'Archive',
        icon: ArchiveIcon,
        onClick: () => {
          onClose();
          onArchive(action);
        }
      });
    }

    if (canUnarchive && onUnarchive) {
      items.push({
        label: 'Unarchive',
        icon: ArchiveIcon,
        onClick: () => {
          onClose();
          onUnarchive(action);
        }
      });
    }

    // Move to a group — available for personal actions (move into a group) and
    // group actions (move to another group), but not PAM actions, whose group
    // follows their linked assessment. Gate on origin_type (authoritative) so a
    // legitimately named group whose prefix is "PAM" isn't misclassified.
    //
    // LIMITATION: PAM actions are intentionally non-movable everywhere (incl.
    // the all-actions page). Their group mirrors the linked SAQ's assigned
    // group and their access is resolved live from the PAM question, so a move
    // can't sever that linkage — the backend rejects it too. See the guard in
    // ActionService.move_action_to_group for the full rationale.
    const isPamAction = action.origin_type === 'pam';
    if (onMove && !isPamAction) {
      items.push({
        label: isActionInGroup ? 'Move to another group' : 'Move to a group',
        icon: ArrowsLeftRightIcon,
        onClick: () => {
          onClose();
          onMove(action);
        }
      });
    }

    return items;
  }, [action, canArchive, canUnarchive, onArchive, onUnarchive, onMove, isActionInGroup, onClose]);

  useEffect(() => {
    if (action) {
      setDescription(action.description);
      setDueDate(action.due_date ? action.due_date.split('T')[0] : ''); // Convert to YYYY-MM-DD format or empty
      setPriority(action.priority ?? '');
      // Look up assignee emails from the assignees array
      let seededEmails: string[] = [];
      if (action.assignees && action.assignees.length > 0) {
        seededEmails = action.assignees
          .map((a) => {
            const user = users.find((u) => u.user_id === a.user_id);
            return user?.email || a.email || null;
          })
          .filter((e): e is string => e !== null);
        setAssigneeEmails(seededEmails);
        assigneeBackfilledRef.current = seededEmails.length > 0;
      } else {
        setAssigneeEmails([]);
        assigneeBackfilledRef.current = false;
      }
      setGroupId(initialGroupId ?? null);
      const seededCost = action.cost != null ? String(action.cost) : '';
      const seededRevenue = action.revenue_cost != null ? String(action.revenue_cost) : '';
      const seededCapital = action.capital_cost != null ? String(action.capital_cost) : '';
      setCost(seededCost);
      setRevenueCost(seededRevenue);
      setCapitalCost(seededCapital);
      const seededTagIds = (action.tags ?? []).filter((t) => t.status !== 'rejected').map((t) => t.tag_id);
      setTagIds(seededTagIds);
      setSavedSnapshot(
        buildFormSnapshot({
          description: action.description,
          dueDate: action.due_date ? action.due_date.split('T')[0] : '',
          priority: action.priority ?? '',
          assigneeEmails: seededEmails,
          cost: seededCost,
          revenueCost: seededRevenue,
          capitalCost: seededCapital,
          tagIds: seededTagIds
        })
      );

      // Load audit logs and extraction source for existing action.
      // Imported action logs are extraction-backed too, so they can provide the
      // same source document and linked evidence as document extractions.
      if (isOpen) {
        loadAuditLogs(action.id);
        // Every reviewed import (document, meeting minutes, action log) now shares
        // the single 'extraction' origin; the specific kind is read back from the
        // extraction source's source_type where a label is needed.
        if (action.origin_type === 'extraction') {
          loadExtractionSource(action.id);
        } else {
          setExtractionSource(null);
        }
      }
    } else {
      // Set default due date to the first of next month
      const firstOfNextMonth = startOfMonth(addMonths(new Date(), 1));
      const formattedDate = format(firstOfNextMonth, 'yyyy-MM-dd');

      const draftDescription = initialDescription ?? '';
      const draftPriority = initialPriority ?? '';
      setDescription(draftDescription);
      setDueDate(formattedDate);
      setPriority(draftPriority);
      setAssigneeEmails([]);
      setGroupId(initialGroupId ?? null);
      setCost('');
      setRevenueCost('');
      setCapitalCost('');
      setTagIds([]);
      setSavedSnapshot(
        buildFormSnapshot({
          description: draftDescription,
          dueDate: formattedDate,
          priority: draftPriority,
          assigneeEmails: [],
          cost: '',
          revenueCost: '',
          capitalCost: '',
          tagIds: []
        })
      );
      setAuditLogs([]);
      setExtractionSource(null);
      assigneeBackfilledRef.current = false;
    }
    setIsEstimatingCost(false);
    setCostEstimate(null);
    setKeptCostEstimateComments([]);
    setErrors({});
    setIncludeInMeeting(!!meetingId);
    setUpdateText('');
    setUpdateMode(null);
    setIsEditing(!action);
    setIsEditingDescription(!action);
    setIsDescriptionExpanded(false);
    setIsDescriptionClamped(false);
  }, [action, isOpen, initialGroupId, meetingId, initialDescription, initialPriority]);

  const defaultCreateTagIds = useMemo(() => {
    const ids: string[] = [];
    if (initialDomainSlug) {
      const domainGroup = tagCatalog.find((group) => group.slug === 'domain');
      const tag = domainGroup?.tags.find((t) => t.slug === initialDomainSlug);
      if (tag) ids.push(tag.id);
    }
    if (preselectPamTag) {
      const nationalReturns = tagCatalog.find((group) => group.slug === 'national_returns');
      const tag = nationalReturns?.tags.find((t) => t.slug === 'pam');
      if (tag) ids.push(tag.id);
    }
    return ids;
  }, [initialDomainSlug, preselectPamTag, tagCatalog]);

  useEffect(() => {
    if (action || !isOpen || defaultCreateTagIds.length === 0) return;
    setTagIds((prev) => {
      const missing = defaultCreateTagIds.filter((id) => !prev.includes(id));
      return missing.length === 0 ? prev : [...prev, ...missing];
    });
    setSavedSnapshot((prev) => {
      const current = prev.tagIds ? prev.tagIds.split(',').filter(Boolean) : [];
      const missing = defaultCreateTagIds.filter((id) => !current.includes(id));
      if (missing.length === 0) return prev;
      return { ...prev, tagIds: [...current, ...missing].sort().join(',') };
    });
  }, [action, isOpen, defaultCreateTagIds]);

  useEffect(() => {
    if (action || !isOpen || !groupId || !groups) return;
    const selected = groups.find((group) => group.id === groupId);
    const slug = selected?.safety_group?.domain ?? selected?.domain ?? null;
    if (!slug) return;
    const domainGroup = tagCatalog.find((group) => group.slug === 'domain');
    const tag = domainGroup?.tags.find((t) => t.slug === slug);
    if (!tag) return;
    setTagIds((prev) => (prev.includes(tag.id) ? prev : [...prev, tag.id]));
  }, [action, isOpen, groupId, groups, tagCatalog]);

  // Backfill assignee emails when associated users refresh, without resetting form fields.
  useEffect(() => {
    if (!action?.assignees?.length) return;
    if (assigneeBackfilledRef.current) return;

    const emails = action.assignees
      .map((a) => {
        const user = users.find((u) => u.user_id === a.user_id);
        return user?.email || a.email || null;
      })
      .filter((e): e is string => e !== null);
    if (emails.length > 0) {
      setAssigneeEmails(emails);
      // This is a system backfill, not a user edit — move the saved baseline in
      // step so it doesn't spuriously mark the form as having unsaved changes.
      setSavedSnapshot((prev) => ({ ...prev, assignees: [...emails].sort().join(',') }));
      assigneeBackfilledRef.current = true;
    }
  }, [action, users]);

  const loadAuditLogs = async (actionId: string) => {
    setIsLoadingAudit(true);
    try {
      const response = await api.getActionAuditLogs(actionId);
      setAuditLogs(response.audit_logs);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setAuditLogs([]);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadExtractionSource = async (actionId: string) => {
    setIsLoadingExtractionSource(true);
    try {
      const response = await api.getActionExtractionSource(actionId);
      setExtractionSource(response);
    } catch (err) {
      console.error('Failed to load extraction source:', err);
      setExtractionSource(null);
    } finally {
      setIsLoadingExtractionSource(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (showCostSection) {
      const revenueError = validateCost(revenueCost);
      if (revenueError) {
        newErrors.revenue_cost = revenueError;
      }
      const capitalError = validateCost(capitalCost);
      if (capitalError) {
        newErrors.capital_cost = capitalError;
      }
    } else {
      const costError = validateCost(cost);
      if (costError) {
        newErrors.cost = costError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCostChange = (value: string) => {
    setCost(value);
    if (errors.cost) {
      const costError = validateCost(value);
      setErrors((prev) => {
        if (costError) {
          return { ...prev, cost: costError };
        }
        const { cost: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleComponentCostChange = (field: 'revenue_cost' | 'capital_cost', value: string) => {
    if (field === 'revenue_cost') {
      setRevenueCost(value);
    } else {
      setCapitalCost(value);
    }
    if (errors[field]) {
      const componentError = validateCost(value);
      setErrors((prev) => {
        if (componentError) {
          return { ...prev, [field]: componentError };
        }
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEstimateCost = useCallback(async () => {
    if (!onEstimateCost) return;
    setCostEstimate(null);
    setIsEstimatingCost(true);
    posthog?.capture('action_cost_estimate_requested', { actionId: action?.id });
    try {
      const estimate = await onEstimateCost();
      setCostEstimate(estimate);
      posthog?.capture('action_cost_estimate_received', {
        actionId: action?.id,
        revenue: estimate.revenue,
        capital: estimate.capital
      });
    } catch (err) {
      posthog?.capture('action_cost_estimate_failed', { actionId: action?.id });
      // The caller maps the endpoint's 404 (unavailable) vs 503 (retryable) — and
      // the null-total guard — into a specific message; surface it as-is.
      toast.error(
        err instanceof Error && err.message ? err.message : "Couldn't generate an AI cost estimate. Please try again."
      );
    } finally {
      setIsEstimatingCost(false);
    }
  }, [onEstimateCost, posthog, action?.id]);

  const handleKeepCostEstimate = useCallback(async () => {
    if (costEstimate === null) return;
    const { revenue, capital, rationale } = costEstimate;
    // Reflect the kept values in the form and activity, then persist immediately:
    // "Keep" both applies AND saves the estimate, so there is no separate Save
    // step. The estimate is only offered for existing (editable) actions, so this
    // is always an update carrying the rationale as an audit progress note.
    setRevenueCost(String(revenue));
    setCapitalCost(String(capital));
    setKeptCostEstimateComments((comments) => [...comments, costEstimate]);
    setCostEstimate(null);
    posthog?.capture('action_cost_estimate_kept', { actionId: action?.id, revenue, capital });
    if (!isEditMode || !canEditFields || !description.trim()) return;
    try {
      await onSave({
        description: description.trim(),
        due_date: dueDate || null,
        priority: priority || null,
        assignee_emails: assigneeEmails.length > 0 ? assigneeEmails : null,
        cost: revenue + capital,
        revenue_cost: revenue,
        capital_cost: capital,
        ai_cost_rationale: rationale,
        meeting_id: includeInMeeting && meetingId ? meetingId : null
      });
      // "Keep" both applied and saved the split. The clean baseline is
      // re-established when the parent refreshes `action` with the persisted
      // response (or closes the modal) — the seed effect reseeds from the
      // canonical server values, so "Save Changes" quiets without us guessing
      // the quantised total here. On failure the kept values stay as unsaved
      // changes to retry.
    } catch {
      // Parent already reported the error; keep the applied values dirty.
    }
  }, [
    costEstimate,
    isEditMode,
    canEditFields,
    description,
    dueDate,
    priority,
    assigneeEmails,
    cost,
    includeInMeeting,
    meetingId,
    onSave,
    posthog,
    action?.id
  ]);

  const handleRejectCostEstimate = useCallback(() => {
    posthog?.capture('action_cost_estimate_rejected', { actionId: action?.id });
    setCostEstimate(null);
  }, [posthog, action?.id]);

  const collectUpdateAttachments = () => {
    const currentUploads = Array.from(updateHub.getUploads().entries()).map(([key, state]) => ({ key, ...state }));
    const hasInflight = currentUploads.some(
      (u) => u.stage === 'uploading' || u.stage === 'scanning' || u.stage === 'processing'
    );
    const hasError = currentUploads.some((u) => u.stage === 'error');
    const documentIds = currentUploads.filter((u) => u.stage === 'ready' && u.documentId).map((u) => u.documentId!);
    return { hasInflight, hasError, documentIds };
  };

  const handleAddUpdate = async () => {
    if (!action || !updateText.trim()) return;
    const { hasInflight, documentIds } = collectUpdateAttachments();
    if (hasInflight) {
      toast.error('Please wait for all files to finish uploading.');
      return;
    }
    setIsSubmittingUpdate(true);
    try {
      const updateMeetingId = includeInMeeting && meetingId ? meetingId : undefined;
      await api.addActionUpdate(action.id, updateText.trim(), documentIds, updateMeetingId);
      setUpdateText('');
      updateHub.clearUploads({ keepInflight: false });
      setUpdateMode(null);
      toast.success('Comment added');
      loadAuditLogs(action.id);
    } catch (err) {
      console.error('Failed to add update:', err);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmittingUpdate(false);
    }
  };

  const handleResolveFromComment = async () => {
    if (!action || !onResolve) return;
    const { hasInflight, hasError, documentIds } = collectUpdateAttachments();
    const text = updateText.trim();
    if (hasInflight) {
      toast.error('Please wait for all files to finish uploading.');
      return;
    }
    if (hasError) {
      toast.error('Some files failed to upload. Please remove them and try again.');
      return;
    }
    if (!text) {
      toast.error('Add a comment to resolve this action.');
      return;
    }
    setIsResolving(true);
    try {
      await onResolve(action, { documentIds, text });
      setUpdateText('');
      updateHub.clearUploads({ keepInflight: false });
      setUpdateMode(null);
      toast.success('Action successfully resolved');
      onClose();
    } catch (err) {
      console.error('Failed to resolve action:', err);
    } finally {
      setIsResolving(false);
    }
  };

  const handleReopenFromComment = async () => {
    if (!action || !onUnresolve) return;
    const { hasInflight, hasError, documentIds } = collectUpdateAttachments();
    const text = updateText.trim();
    if (hasInflight) {
      toast.error('Please wait for all files to finish uploading.');
      return;
    }
    if (hasError) {
      toast.error('Some files failed to upload. Please remove them and try again.');
      return;
    }
    if (!text) {
      toast.error('Add a comment to reopen this action.');
      return;
    }

    setIsResolving(true);
    try {
      await onUnresolve(action, { documentIds, text });
      setUpdateText('');
      updateHub.clearUploads({ keepInflight: false });
      setUpdateMode(null);
      loadAuditLogs(action.id);
    } catch (err) {
      console.error('Failed to reopen action:', err);
    } finally {
      setIsResolving(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      updateHub.uploadFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!isEditable) return false;
    if (!validate()) return false;

    const revenue = revenueCost.trim() === '' ? null : Number(revenueCost.replaceAll(',', ''));
    const capital = capitalCost.trim() === '' ? null : Number(capitalCost.replaceAll(',', ''));
    // With the cost section, the total is derived from the components; the
    // backend recomputes it too, but we send it so callers/optimistic UI agree.
    // When neither component is set we keep the existing (possibly legacy,
    // unclassified) total so editing an unrelated field doesn't wipe it.
    const derivedTotal =
      revenue !== null || capital !== null ? (revenue ?? 0) + (capital ?? 0) : cost.trim() === '' ? null : Number(cost);

    const costFields = showCostSection
      ? { cost: derivedTotal, revenue_cost: revenue, capital_cost: capital }
      : {
          revenue_cost: cost.trim() === '' ? null : Number(cost),
          capital_cost: null
        };

    const data: ActionCreate | ActionUpdate = {
      description: description.trim(),
      due_date: dueDate || null,
      priority: priority || null,
      assignee_emails: assigneeEmails.length > 0 ? assigneeEmails : null,
      ...costFields,
      ...(isEditMode ? { meeting_id: includeInMeeting && meetingId ? meetingId : null } : { group_id: groupId }),
      tag_ids: tagIds
    };

    try {
      await onSave(data);
      // Don't re-baseline from the local form here: the total for a split action
      // is derived server-side (and the split may be quantised), so the saved
      // state can differ from what was typed. On success the parent refreshes
      // `action` with the persisted response (or closes the modal), and the seed
      // effect re-establishes the clean baseline from the canonical values. On
      // failure the parent re-throws, so we skip that and keep the form dirty.
      return true;
    } catch {
      // Parent already reported the error; keep the unsaved state for retry.
      return false;
    }
  };

  const formatAuditValue = (key: string, value: any) => {
    // For assignee_ids (new multi-assignee format), show names
    if (key === 'assignee_ids') {
      if (!value || !Array.isArray(value) || value.length === 0) return 'Unassigned';
      return value
        .map((uid: number) => {
          const user = users.find((u) => u.user_id === uid);
          if (user) {
            return user.firstname && user.surname
              ? `${user.firstname} ${user.surname}`
              : extractNameFromEmail(user.email);
          }
          return `User #${uid}`;
        })
        .join(', ');
    }

    // For due_date, format nicely
    if (key === 'due_date') {
      return formatShortDate(value, { empty: 'No due date' });
    }

    if (key === 'priority') {
      if (!value) return 'Not set';
      return actionPriorityLabel(value as ActionPriority) || value;
    }

    // For status, format nicely
    if (key === 'status') {
      const statusLabels: Record<string, string> = {
        open: 'Open',
        resolved: 'Resolved',
        archived: 'Archived'
      };
      return statusLabels[value] || value;
    }

    if (
      ['cost', 'revenue_cost', 'capital_cost'].includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== ''
    ) {
      const amount = Number(value);
      if (Number.isFinite(amount)) {
        return `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }

    return value;
  };

  const formatFieldLabel = (key: string) => {
    const fieldLabels: Record<string, string> = {
      assignee_ids: 'Assignees',
      due_date: 'Due date',
      priority: 'Priority',
      description: 'Description',
      status: 'Status'
    };
    return fieldLabels[key] || snakeToTitleCase(key);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserName = (userId: number | null | undefined) => {
    if (!userId) return 'System';
    const user = users.find((u) => u.user_id === userId);
    if (!user) return 'Unknown User';
    if (user.firstname && user.surname) {
      return `${user.firstname} ${user.surname}`;
    }
    return extractNameFromEmail(user.email);
  };

  const renderActivityRow = (
    key: string | number,
    userId: number | null | undefined,
    createdAt: string,
    body: ReactNode,
    align: 'center' | 'start' = 'center'
  ) => {
    const user = userId ? users.find((u) => u.user_id === userId) : undefined;
    return (
      <div
        key={key}
        className={`flex min-h-[22px] gap-2.5 py-0.5 ${align === 'start' ? 'items-start' : 'items-center'}`}
      >
        <div className="relative shrink-0 group/avatar">
          <UserAvatar
            firstname={user?.firstname}
            surname={user?.surname}
            email={user?.email}
            name={getUserName(userId)}
            size="xs"
            showTooltip={false}
          />
          <span className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs leading-tight text-white opacity-0 group-hover/avatar:opacity-100">
            <span className="block font-medium">{getUserName(userId)}</span>
            {user?.email && <span className="block text-gray-300">{user.email}</span>}
          </span>
        </div>
        <div className="min-w-0 flex-1 leading-[22px]">{body}</div>
        <span className="shrink-0 whitespace-nowrap text-xs leading-[22px]">{formatDate(createdAt)}</span>
      </div>
    );
  };

  const descriptionParagraphs = useMemo(() => {
    return description.split(/\r?\n/);
  }, [description]);

  useEffect(() => {
    if (isEditingDescription || isDescriptionExpanded) {
      setIsDescriptionClamped(false);
      return;
    }
    const node = descriptionPreviewRef.current;
    if (!node) return;
    setIsDescriptionClamped(node.scrollHeight > node.clientHeight + 1);
  }, [description, isEditingDescription, isDescriptionExpanded]);

  const dueDateDisplay = useMemo(() => {
    if (!dueDate) return null;
    return formatLongDate(dueDate);
  }, [dueDate]);

  const assetReviewSource = action?.links?.find((link) => link.resource_type === 'asset_review');
  const otherResourceLinks = (action?.links ?? []).filter((link) => link.resource_type !== 'asset_review');

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        className="min-h-[36rem] w-full max-w-[calc(66ch+16rem+4rem)]"
        headerClassName="-mx-5 -mt-5 border-b border-border-subtle px-5 pt-4 pb-3"
        closeButtonSize="md"
        title={
          <div className="flex flex-1 items-center min-w-0 pr-2">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="text-xl font-semibold leading-none text-gray-900">
                {isEditMode ? 'Action' : 'Create Action'}
              </h2>
              {isEditMode && action?.reference_id && (
                <span className="inline-flex h-7 items-center rounded bg-gray-100 px-2 text-xs font-mono leading-none text-content-subtle whitespace-nowrap">
                  {action.reference_id}
                </span>
              )}
              {isEditMode && action && <ActionStatusPill status={getActionDisplayStatus(action)} />}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {isEditMode && isEditable && !isEditing && (
                <Button
                  onClick={() => {
                    setIsEditing(true);
                    setIsEditingDescription(true);
                  }}
                  variant="outline"
                  size="md"
                  text="Edit"
                  icon={PencilSimpleIcon}
                  loading={isLoading}
                  disabled={isLoading}
                />
              )}
              {isEditMode && isEditable && isEditing && (
                <>
                  <Button onClick={handleCancelEdit} variant="outline" size="md" text="Cancel" disabled={isLoading} />
                  <Button
                    onClick={async () => {
                      const saved = await handleSubmit();
                      if (saved) {
                        setIsEditing(false);
                        setIsEditingDescription(false);
                      }
                    }}
                    variant="primary"
                    size="md"
                    text="Save Changes"
                    loading={isLoading}
                    disabled={isLoading || !hasUnsavedChanges}
                  />
                </>
              )}
              {isEditMode && actionMenuItems.length > 0 && (
                <Dropdown
                  className="inline-flex shrink-0 items-center leading-none"
                  triggerClassName="inline-flex items-center leading-none"
                  trigger={
                    <Button
                      variant="outline"
                      size="md"
                      icon={DotsThreeIcon}
                      aria-label="More actions"
                      disabled={isLoading}
                    />
                  }
                  options={actionMenuItems.map((item) => {
                    const ItemIcon = item.icon;
                    return {
                      value: item.label,
                      label: item.label,
                      disabled: item.disabled || isLoading,
                      icon: ItemIcon ? <ItemIcon size={16} {...item.iconProps} /> : undefined
                    };
                  })}
                  value=""
                  onChange={(value) => {
                    const item = actionMenuItems.find((menuItem) => menuItem.label === value);
                    item?.onClick();
                  }}
                  placement="bottom-end"
                  menuWidth={220}
                />
              )}
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-4 flex-1 min-h-0 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,66ch)_16rem] gap-1 flex-1 min-h-0">
            <div className="flex min-h-0 flex-col">
              <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1.5 pb-1.5 pt-1">
                <div>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label
                      htmlFor={canEditFields && isEditingDescription ? descriptionFieldId : undefined}
                      className="block text-sm font-medium text-content-subtle"
                    >
                      Description
                    </label>
                  </div>
                  {canEditFields && isEditingDescription ? (
                    <Textarea
                      id={descriptionFieldId}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe what needs to be done..."
                      rows={6}
                      disabled={isLoading}
                      className={errors.description ? 'border-red-500' : ''}
                    />
                  ) : (
                    <div>
                      <div
                        id={descriptionFieldId}
                        ref={descriptionPreviewRef}
                        className={`text-sm text-content-default font-normal ${
                          !isDescriptionExpanded ? 'line-clamp-6' : ''
                        }`}
                      >
                        {description.trim() ? (
                          descriptionParagraphs.map((paragraph, index) =>
                            paragraph.trim() ? (
                              <p key={`${index}-${paragraph}`} className={index > 0 ? 'mt-2' : ''}>
                                <DescriptionText text={paragraph} />
                              </p>
                            ) : (
                              <p key={`empty-${index}`} className="mt-2 h-4" aria-hidden="true" />
                            )
                          )
                        ) : (
                          <p className="text-content-subtle">No description</p>
                        )}
                      </div>
                      {(isDescriptionClamped || isDescriptionExpanded) && (
                        <button
                          type="button"
                          className="mt-1 text-xs font-medium text-misc-link hover:underline"
                          onClick={(event) => {
                            event.stopPropagation();
                            setIsDescriptionExpanded((prev) => !prev);
                          }}
                        >
                          {isDescriptionExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  )}
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>

                {isEditMode && action && (
                  <div>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-700">Activity</p>
                      <Dropdown
                        className="shrink-0"
                        trigger={
                          <Button
                            variant="outline"
                            size="sm"
                            text={
                              activityFilter === 'all'
                                ? 'All updates'
                                : activityFilter === 'status'
                                  ? 'Status changes'
                                  : 'Comments'
                            }
                            icon={CaretDownIcon}
                            iconPosition="right"
                            aria-label="Filter activity"
                          />
                        }
                        options={[
                          { label: 'All updates', value: 'all' },
                          { label: 'Status changes', value: 'status' },
                          { label: 'Comments', value: 'comments' }
                        ]}
                        value={activityFilter}
                        onChange={(value) => setActivityFilter(value as 'all' | 'status' | 'comments')}
                        placement="bottom-end"
                        menuWidth={160}
                      />
                    </div>
                    {isLoadingAudit ? (
                      <div className="py-2">
                        <Loading size="sm" text="Loading activity..." />
                      </div>
                    ) : auditLogs.length === 0 && keptCostEstimateComments.length === 0 ? null : (
                      <div className="space-y-2 text-xs text-content-subtle">
                        {filteredAuditLogs.map((log) => {
                          const isTextUpdate = !!log.text_update;
                          const isCreate = log.is_create;

                          // Text update: show text content with files
                          if (isTextUpdate) {
                            return renderActivityRow(
                              log.id,
                              log.text_update.user_id,
                              log.created_at,
                              <>
                                <p className="whitespace-pre-wrap text-sm leading-[22px] text-content-default">
                                  {log.text_update.text}
                                </p>
                                {log.text_update.files?.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-2">
                                    {log.text_update.files.map((file: any) => (
                                      <button
                                        key={file.id}
                                        onClick={async () => {
                                          try {
                                            if (file.content_type?.startsWith('image/')) {
                                              const response = await getDocumentLinkByExternalId(file.external_id);
                                              setImagePreview({
                                                url: response.url,
                                                filename: file.title,
                                                documentId: file.external_id
                                              });
                                            } else {
                                              tenantNavigate(`/wiki/${file.external_id}`);
                                            }
                                          } catch (err) {
                                            console.error('Failed to open file:', err);
                                            toast.error('Failed to open file');
                                          }
                                        }}
                                        className="flex items-center gap-1 text-[10px] bg-gray-50 px-1.5 py-0.5 rounded border hover:bg-gray-100 transition"
                                      >
                                        <PaperclipIcon size={10} />
                                        <span className="truncate max-w-[120px]">{file.title}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>,
                              'start'
                            );
                          }

                          // Create: show "Created by XIcon" with origin info
                          if (isCreate) {
                            return renderActivityRow(
                              log.id,
                              log.user_id,
                              log.created_at,
                              <p>
                                Created
                                {action.origin_type === 'asset_review' && (
                                  <>
                                    {' '}
                                    from{' '}
                                    {assetReviewSource ? (
                                      <ResourceLink
                                        variant="prose"
                                        content={assetReviewSource.title}
                                        data={{
                                          id: assetReviewSource.resource_id,
                                          resource_id: assetReviewSource.resource_id,
                                          resource_type: assetReviewSource.resource_type,
                                          title: assetReviewSource.title,
                                          is_wiki: false
                                        }}
                                      />
                                    ) : (
                                      'an asset review'
                                    )}
                                  </>
                                )}
                                {action.origin_type === 'extraction' && (
                                  <>
                                    {' '}
                                    from{' '}
                                    {extractionSource?.source_type === 'meeting_minutes'
                                      ? 'meeting minutes'
                                      : extractionSource?.source_type === 'action_log'
                                        ? 'an action log'
                                        : 'a document'}
                                    {action.origin_reference_id && (
                                      <>
                                        {' '}
                                        <button
                                          onClick={() => window.open(`/wiki/${action.origin_reference_id}`, '_blank')}
                                          className="font-medium text-misc-link pl-1 underline underline-offset-2 hover:text-primary-default"
                                        >
                                          View source
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </p>
                            );
                          }

                          // Move: show "XIcon moved this to <group>" (from <group> when known)
                          if (log.moved) {
                            const to = log.moved.to_group_name ?? 'another group';
                            const from = log.moved.from_group_name;
                            return renderActivityRow(
                              log.id,
                              log.user_id,
                              log.created_at,
                              <p>
                                Moved this to <span className="font-medium">{to}</span>
                                {from ? ` (from ${from})` : ''}
                              </p>
                            );
                          }

                          // Field changes: show all changed fields together with one timestamp
                          if (log.old_value && log.new_value) {
                            const changedFields = Object.keys(log.new_value).filter(
                              (key) => key !== 'update_id' && log.old_value[key] !== log.new_value[key]
                            );

                            if (changedFields.length === 0) return null;

                            const author = getUserName(log.user_id);
                            const otherFields = changedFields.filter((key) => key !== 'description');
                            const descriptionChanged = changedFields.includes('description');

                            return renderActivityRow(
                              log.id,
                              log.user_id,
                              log.created_at,
                              descriptionChanged && otherFields.length === 0 ? (
                                <button
                                  type="button"
                                  className="text-left hover:underline"
                                  onClick={() =>
                                    setDescriptionDiff({
                                      author,
                                      oldValue: String(log.old_value.description ?? ''),
                                      newValue: String(log.new_value.description ?? '')
                                    })
                                  }
                                >
                                  Description changed
                                </button>
                              ) : (
                                <div className="space-y-0.5">
                                  {descriptionChanged && (
                                    <button
                                      type="button"
                                      className="text-left hover:underline"
                                      onClick={() =>
                                        setDescriptionDiff({
                                          author,
                                          oldValue: String(log.old_value.description ?? ''),
                                          newValue: String(log.new_value.description ?? '')
                                        })
                                      }
                                    >
                                      Description changed
                                    </button>
                                  )}
                                  {otherFields.map((key) => {
                                    const label = formatFieldLabel(key);
                                    const oldVal = formatAuditValue(key, log.old_value[key]);
                                    const newVal = formatAuditValue(key, log.new_value[key]);

                                    return (
                                      <div key={key}>
                                        {label}: {oldVal} → {newVal}
                                      </div>
                                    );
                                  })}
                                </div>
                              ),
                              changedFields.length > 1 ? 'start' : 'center'
                            );
                          }

                          return null;
                        })}
                        {filteredAuditLogs.length === 0 && activityFilter !== 'all' && (
                          <p className="text-xs text-content-subtle">
                            {activityFilter === 'status' ? 'No status changes.' : 'No comments.'}
                          </p>
                        )}
                        {activityFilter === 'all' &&
                          keptCostEstimateComments.map((estimate, index) =>
                            renderActivityRow(
                              `${estimate.revenue}-${estimate.capital}-${index}`,
                              currentUserId,
                              new Date().toISOString(),
                              <>
                                <p>
                                  Kept an AI cost estimate of £
                                  {(estimate.revenue + estimate.capital).toLocaleString('en-GB', {
                                    minimumFractionDigits: 2
                                  })}{' '}
                                  ({`£${estimate.revenue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}{' '}
                                  revenue and{' '}
                                  {`£${estimate.capital.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}{' '}
                                  capital).
                                </p>
                                <p className="mt-1 whitespace-pre-line">{estimate.rationale}</p>
                              </>,
                              'start'
                            )
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isEditMode &&
                action &&
                !isArchived &&
                (canAddProgressUpdates || (canResolve && onResolve) || (canUnresolve && onUnresolve)) && (
                  <div className="shrink-0 border-t border-border-subtle px-1 pt-2">
                    {updateMode === null ? (
                      <div className="flex items-center justify-end gap-2">
                        {canAddProgressUpdates && (
                          <Button
                            type="button"
                            variant="primary"
                            size="md"
                            text="Add update"
                            onClick={() => setUpdateMode('comment')}
                          />
                        )}
                        {canResolve && onResolve && (
                          <Button
                            type="button"
                            variant="outline"
                            size="md"
                            text="Resolve"
                            icon={CheckCircleIcon}
                            onClick={() => setUpdateMode('resolve')}
                          />
                        )}
                        {canUnresolve && onUnresolve && (
                          <Button
                            type="button"
                            variant="outline"
                            size="md"
                            text="Reopen"
                            icon={ArrowCounterClockwiseIcon}
                            onClick={() => setUpdateMode('reopen')}
                          />
                        )}
                      </div>
                    ) : (
                      <>
                        <label htmlFor={updateFieldId} className="block text-sm font-medium text-gray-700 mb-1">
                          {updateMode === 'resolve'
                            ? 'Resolve action'
                            : updateMode === 'reopen'
                              ? 'Reopen action'
                              : 'Add update'}
                        </label>
                        <div className="overflow-hidden rounded-md border-2 border-border-default bg-white focus-within:border-border-active">
                          <Textarea
                            id={updateFieldId}
                            value={updateText}
                            onChange={(e) => setUpdateText(e.target.value)}
                            placeholder={
                              updateMode === 'resolve'
                                ? 'Explain why this action is resolved...'
                                : updateMode === 'reopen'
                                  ? 'Explain why this action is being reopened...'
                                  : 'Add a comment...'
                            }
                            rows={2}
                            fullWidth={true}
                            wrapperClassName="!border-0 rounded-none"
                          />

                          {updateHub.uploads.size > 0 && (
                            <div className="flex flex-wrap gap-2 px-3 pb-2">
                              {Array.from(updateHub.uploads.entries()).map(([key, state]) => (
                                <div
                                  key={key}
                                  className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs"
                                >
                                  {state.stage === 'ready' ? (
                                    <CheckCircleIcon size={12} className="text-green-500" />
                                  ) : state.stage === 'error' ? (
                                    <XIcon size={12} className="text-red-500" />
                                  ) : (
                                    <SpinnerGapIcon size={12} className="animate-spin text-blue-500" />
                                  )}
                                  <span className="truncate max-w-[150px]">{state.file.name}</span>
                                  <button
                                    onClick={() => updateHub.removeFile(key)}
                                    className="text-gray-500 hover:text-red-500"
                                  >
                                    <XIcon size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex justify-between items-center gap-3 border-t border-border-subtle px-3 py-2">
                            <div className="flex items-center gap-3">
                              <Button
                                type="button"
                                variant="ghost"
                                size="md"
                                icon={PaperclipIcon}
                                text="Attach files"
                                onClick={() => fileInputRef.current?.click()}
                              />
                              {meetingId && !canEditFields && (
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <Checkbox
                                    checked={includeInMeeting}
                                    onChange={(e) => setIncludeInMeeting(e.target.checked)}
                                  />
                                  <span className="text-xs text-content-subtle">Include in meeting</span>
                                </label>
                              )}
                            </div>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              className="hidden"
                              multiple
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="md"
                                text="Cancel"
                                onClick={() => {
                                  setUpdateText('');
                                  updateHub.clearUploads({ keepInflight: false });
                                  setUpdateMode(null);
                                }}
                                disabled={isSubmittingUpdate || isResolving}
                              />
                              {updateMode === 'comment' && (
                                <Button
                                  onClick={handleAddUpdate}
                                  variant="primary"
                                  size="md"
                                  text="Post update"
                                  disabled={
                                    !updateText.trim() || isSubmittingUpdate || isResolving || hasInflightUploads
                                  }
                                  loading={isSubmittingUpdate || hasInflightUploads}
                                />
                              )}
                              {updateMode === 'resolve' && (
                                <Button
                                  onClick={handleResolveFromComment}
                                  variant="primary"
                                  size="md"
                                  text="Resolve action"
                                  icon={CheckCircleIcon}
                                  disabled={
                                    isSubmittingUpdate || isResolving || hasInflightUploads || !updateText.trim()
                                  }
                                  loading={isResolving}
                                />
                              )}
                              {updateMode === 'reopen' && (
                                <Button
                                  onClick={handleReopenFromComment}
                                  variant="primary"
                                  size="md"
                                  text="Reopen action"
                                  icon={ArrowCounterClockwiseIcon}
                                  disabled={
                                    isSubmittingUpdate || isResolving || hasInflightUploads || !updateText.trim()
                                  }
                                  loading={isResolving}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>

            <div className="custom-scrollbar flex min-h-0 flex-col overflow-y-auto text-xs text-content-subtle [&>*]:pb-3 [&>*+*]:border-t [&>*+*]:border-border-subtle [&>*+*]:pt-3 lg:border-l lg:border-border-subtle lg:pl-6">
              {canEditFields ? (
                <ActionAssigneeMultiSelector
                  id={assigneeFieldId}
                  value={assigneeEmails}
                  onChange={setAssigneeEmails}
                  onOpen={handleAssigneeSelectorOpen}
                  label="Assignees"
                  labelClassName="block text-xs font-medium text-content-subtle mb-1"
                  placeholder="Select or enter email..."
                  disabled={isLoading}
                  users={users}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Assignees</label>
                  <div id={assigneeFieldId}>
                    {assigneeEmails.length > 0 ? (
                      assigneeEmails.map((email) => {
                        const user = users.find((u) => u.email.toLowerCase() === email.toLowerCase());
                        const name =
                          user && user.firstname && user.surname
                            ? `${user.firstname} ${user.surname}`
                            : extractNameFromEmail(email);
                        return (
                          <div key={email} className="flex items-center gap-1.5 text-xs text-content-subtle">
                            <UserAvatar
                              firstname={user?.firstname}
                              surname={user?.surname}
                              email={email}
                              name={name}
                              size="xs"
                            />
                            <span>{name}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-content-subtle">Unassigned</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor={canEditFields ? dueDateFieldId : undefined}
                  className="block text-xs font-medium text-content-subtle mb-1"
                >
                  Due Date
                </label>
                {canEditFields ? (
                  <DateInput
                    id={dueDateFieldId}
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={isLoading}
                    wrapperClassName={errors.dueDate ? 'border-red-500' : ''}
                  />
                ) : (
                  <p id={dueDateFieldId} className="text-xs text-content-subtle">
                    {dueDateDisplay || <span className="text-content-subtle">No due date</span>}
                  </p>
                )}
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>

              <div>
                <label
                  htmlFor={canEditFields ? priorityFieldId : undefined}
                  className="block text-xs font-medium text-content-subtle mb-1"
                >
                  Priority
                </label>
                {canEditFields ? (
                  <Dropdown
                    id={priorityFieldId}
                    options={PRIORITY_DROPDOWN_OPTIONS}
                    initialValue={priority}
                    onChange={(value) => setPriority((value as ActionPriority | '') || '')}
                    placeholder="Not set"
                    width="w-full"
                    size="md"
                    disabled={isLoading}
                    ariaLabel="Priority"
                  />
                ) : (
                  <div id={priorityFieldId}>
                    {priority ? (
                      <ActionPriorityPill priority={priority} />
                    ) : (
                      <span className="text-xs text-content-subtle">Not set</span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="block text-xs font-medium text-content-subtle mb-1">Tags</p>
                {canEditFields ? (
                  <TagPillSelect
                    groups={tagCatalog.map((group) => ({
                      slug: group.slug,
                      label: group.label,
                      cardinality: 'multiple',
                      options: group.tags.map((t) => ({
                        id: t.id,
                        label: t.label,
                        group: group.slug
                      }))
                    }))}
                    value={tagIds}
                    onChange={setTagIds}
                    placeholder="Not set"
                    disabled={isLoading}
                    ariaLabel="Tags"
                  />
                ) : (
                  <ActionTagPills tags={action?.tags} maxVisible={4} />
                )}
              </div>

              {(isEditMode || showGroupOnCreate) && (canSelectGroupOnCreate || groupDisplayLabel) && (
                <div>
                  <p className="text-xs font-medium text-content-subtle mb-1">Group</p>
                  {canSelectGroupOnCreate ? (
                    <Dropdown
                      options={[{ value: '', label: 'Not set' }, ...createGroupOptions]}
                      initialValue={groupId ?? ''}
                      onChange={(value) => setGroupId(value ? String(value) : null)}
                      width="w-full"
                      ariaLabel="Group"
                      disabled={isLoading}
                    />
                  ) : groupDisplayResourceId ? (
                    <ResourceLink
                      content={groupDisplayLabel ?? 'Group'}
                      maxLabelLength={26}
                      data={{
                        id: groupDisplayResourceId,
                        resource_id: groupDisplayResourceId,
                        resource_type: 'team_group',
                        title: groupDisplayLabel ?? 'Group',
                        is_wiki: false
                      }}
                    />
                  ) : (
                    <p className="p-1 text-xs text-content-subtle">{groupDisplayLabel}</p>
                  )}
                  {!isEditMode && !canSelectGroupOnCreate && groupCreationHint && (
                    <p className="mt-1 text-xs text-content-subtle">{groupCreationHint}</p>
                  )}
                </div>
              )}

              {isEditMode && action && (isLoadingExtractionSource || extractionSource || assetReviewSource) && (
                <div className="">
                  <p className="text-xs font-medium text-content-subtle mb-1">Source Document</p>
                  {isLoadingExtractionSource ? (
                    <div className="py-2">
                      <Loading size="sm" text="Loading source..." />
                    </div>
                  ) : extractionSource ? (
                    <div className="space-y-2 ">
                      {/* Document name */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {extractionSource.document_external_id ? (
                          <ResourceLink
                            content={extractionSource.document_name}
                            maxLabelLength={20}
                            data={{
                              id: extractionSource.document_external_id,
                              resource_id: extractionSource.document_external_id,
                              resource_type: 'document',
                              title: extractionSource.document_name,
                              is_wiki: false
                            }}
                          />
                        ) : (
                          <p className="text-xs text-content-subtle">{extractionSource.document_name}</p>
                        )}
                        {extractionSource.source_elements?.map((sourceElement, elemIndex) => (
                          <ActionSourceTooltip
                            key={sourceElement.id || elemIndex}
                            sourceElement={sourceElement}
                            documentExternalId={extractionSource.document_external_id}
                            clickable={!!(extractionSource.document_external_id && sourceElement.id)}
                          >
                            <span
                              className={`inline-flex items-center text-xs bg-blue-100 px-2 py-0.5 rounded ${
                                extractionSource.document_external_id && sourceElement.id
                                  ? 'hover:bg-gray-200 cursor-pointer transition-colors'
                                  : ''
                              }`}
                              onClick={(e) => {
                                if (extractionSource.document_external_id && sourceElement.id) {
                                  e.stopPropagation();
                                  posthog?.capture('actions_source_document_clicked', {
                                    actionId: action?.id,
                                    extractionId: extractionSource.extraction_id,
                                    elementId: sourceElement.id
                                  });
                                  const tenantPathPrefix = tenantCode ? `/${tenantCode}` : '';
                                  const returnTo = `${location.pathname.startsWith(tenantPathPrefix) ? location.pathname.slice(tenantPathPrefix.length) || '/' : location.pathname}${location.search}`;
                                  const reviewParams = new URLSearchParams({
                                    tab: 'action-review',
                                    extraction: extractionSource.extraction_id,
                                    action: action.id,
                                    source: sourceElement.id,
                                    return_to: returnTo
                                  });
                                  window.open(
                                    toTenantPath(
                                      `/wiki/${extractionSource.document_external_id}?${reviewParams.toString()}`,
                                      tenantCode
                                    ),
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                                }
                              }}
                            >
                              <span className="text-content-default font-medium">
                                Page {sourceElement.page_index > 0 ? sourceElement.page_index : elemIndex + 1}
                              </span>
                            </span>
                          </ActionSourceTooltip>
                        ))}
                      </div>

                      {(extractionSource.suggested_priority || extractionSource.suggested_timeframe) && (
                        <p className="text-xs text-content-subtle">
                          {extractionSource.suggested_timeframe && (
                            <span>Suggested timeframe: {extractionSource.suggested_timeframe}</span>
                          )}
                          {extractionSource.suggested_timeframe && extractionSource.suggested_priority && (
                            <span className="mx-1">·</span>
                          )}
                          {extractionSource.suggested_priority && (
                            <span>
                              Suggested priority: {formatSuggestedPriority(extractionSource.suggested_priority)}
                            </span>
                          )}
                        </p>
                      )}

                      {extractionSource.spreadsheet_source_refs?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {extractionSource.spreadsheet_source_refs.map((source) => (
                            <button
                              key={source.id}
                              className="inline-flex items-center rounded bg-blue-100 px-1 py-0.5 text-xs transition-colors hover:bg-gray-200"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!extractionSource.document_external_id || !action?.id) return;
                                posthog?.capture('actions_spreadsheet_source_clicked', {
                                  actionId: action.id,
                                  extractionId: extractionSource.extraction_id,
                                  sourceId: source.id
                                });
                                const tenantPathPrefix = tenantCode ? `/${tenantCode}` : '';
                                const returnTo = `${location.pathname.startsWith(tenantPathPrefix) ? location.pathname.slice(tenantPathPrefix.length) || '/' : location.pathname}${location.search}`;
                                const reviewParams = new URLSearchParams({
                                  tab: 'action-review',
                                  extraction: extractionSource.extraction_id,
                                  action: action.id,
                                  source: source.id,
                                  return_to: returnTo
                                });
                                window.open(
                                  toTenantPath(
                                    `/wiki/${extractionSource.document_external_id}?${reviewParams.toString()}`,
                                    tenantCode
                                  ),
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }}
                              type="button"
                            >
                              {source.sheet_name} · {source.cell_range}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : assetReviewSource ? (
                    <ResourceLink
                      content={assetReviewSource.title}
                      maxLabelLength={20}
                      data={{
                        id: assetReviewSource.resource_id,
                        resource_id: assetReviewSource.resource_id,
                        resource_type: assetReviewSource.resource_type,
                        title: assetReviewSource.title,
                        is_wiki: false
                      }}
                    />
                  ) : null}
                </div>
              )}

              {showCostSection ? (
                <ActionCostSection
                  revenueCost={revenueCost}
                  capitalCost={capitalCost}
                  onRevenueChange={(value) => handleComponentCostChange('revenue_cost', value)}
                  onCapitalChange={(value) => handleComponentCostChange('capital_cost', value)}
                  errors={{ revenue_cost: errors.revenue_cost, capital_cost: errors.capital_cost }}
                  isEditable={canEditFields}
                  isLoading={isLoading}
                  storedTotal={action?.cost ?? null}
                  onEstimate={onEstimateCost ? handleEstimateCost : undefined}
                  isEstimating={isEstimatingCost}
                  estimateSection={
                    canEditFields && (isEstimatingCost || costEstimate) ? (
                      <div className="mt-3 overflow-hidden rounded-xl border border-blue-200 bg-blue-50">
                        <div className="flex h-[52px] items-center gap-2 px-4">
                          <span className="flex-1 text-sm font-medium text-content-default">AI cost estimate</span>
                          {costEstimate && (
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
                              High confidence
                            </span>
                          )}
                        </div>
                        <div className="border-t border-blue-200 px-4 py-4">
                          {costEstimate ? (
                            <div>
                              <p className="whitespace-pre-line text-sm italic leading-6 text-content-default">
                                {costEstimate.rationale}
                              </p>
                              <dl className="mt-4 space-y-2 text-sm text-content-default">
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <dt className="font-medium">Revenue spend</dt>
                                    <dd className="text-content-subtle">
                                      Ongoing staff time, maintenance, and operating costs
                                    </dd>
                                  </div>
                                  <dd className="shrink-0 font-medium lining-nums tabular-nums">
                                    £{costEstimate.revenue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                  </dd>
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <dt className="font-medium">Capital spend</dt>
                                    <dd className="text-content-subtle">
                                      One-off equipment and project delivery costs
                                    </dd>
                                  </div>
                                  <dd className="shrink-0 font-medium lining-nums tabular-nums">
                                    £{costEstimate.capital.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                  </dd>
                                </div>
                              </dl>
                              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-3">
                                <p className="font-medium text-content-default">
                                  Total estimate:{' '}
                                  <span className="lining-nums tabular-nums">
                                    £
                                    {(costEstimate.revenue + costEstimate.capital).toLocaleString('en-GB', {
                                      minimumFractionDigits: 2
                                    })}
                                  </span>
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Button variant="primary" size="sm" onClick={handleKeepCostEstimate}>
                                    Keep
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={handleRejectCostEstimate}>
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-content-subtle">Generating AI cost estimate…</p>
                          )}
                        </div>
                      </div>
                    ) : undefined
                  }
                />
              ) : (
                <div>
                  <label
                    htmlFor={canEditFields ? costFieldId : undefined}
                    className="block text-xs font-medium text-content-subtle mb-1"
                  >
                    Cost (£) <span className="font-normal text-content-subtle">(optional)</span>
                  </label>
                  {canEditFields ? (
                    <InputField
                      id={costFieldId}
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={cost}
                      onChange={(e) => handleCostChange(e.target.value)}
                      disabled={isLoading}
                      placeholder="e.g. 1500.00"
                      fullWidth
                      wrapperClassName={errors.cost ? 'border-red-500' : ''}
                    />
                  ) : (
                    <p id={costFieldId} className="p-1 text-xs text-content-subtle">
                      {cost.trim() === '' ? (
                        <span className="text-content-subtle">No cost set</span>
                      ) : (
                        `£${Number(cost).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      )}
                    </p>
                  )}
                  {errors.cost && <p className="text-red-500 text-sm mt-1">{errors.cost}</p>}
                </div>
              )}

              {otherResourceLinks.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Resource</label>
                  <div className="flex flex-wrap gap-2">
                    {otherResourceLinks.map((link) => (
                      <ResourceLink
                        key={link.resource_id}
                        content={link.title}
                        data={{
                          id: link.resource_id,
                          resource_id: link.resource_id,
                          resource_type: link.resource_type,
                          title: link.title,
                          is_wiki: false
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {isEditMode && canEditFields && meetingId && (
                <label className="flex items-center gap-2 cursor-pointer select-none mt-auto pt-2 border-t border-border-subtle">
                  <Checkbox checked={includeInMeeting} onChange={(e) => setIncludeInMeeting(e.target.checked)} />
                  <span className="text-xs text-content-subtle">
                    Update as part of {meetingTitle ? `"${meetingTitle}"` : 'this meeting'}
                  </span>
                </label>
              )}

              {!isEditMode && (
                <div className="flex justify-end gap-2 pt-2 mt-auto border-t border-border-subtle">
                  <Button onClick={onClose} variant="outline" text="Cancel" disabled={isLoading} />
                  <Button
                    onClick={handleSubmit}
                    variant="primary"
                    text="Create Action"
                    loading={isLoading}
                    disabled={!canEditFields || isLoading || !description.trim()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {imagePreview && (
        <ImageModal
          open
          url={imagePreview.url}
          filename={imagePreview.filename}
          documentId={imagePreview.documentId}
          onClose={() => setImagePreview(null)}
        />
      )}

      <Modal
        open={!!descriptionDiff}
        onClose={() => setDescriptionDiff(null)}
        className="w-full max-w-2xl"
        title={<h2 className="text-xl font-semibold text-gray-900">Description change</h2>}
      >
        {descriptionDiff && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-content-subtle">{descriptionDiff.author} updated the description</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Previous</p>
                <div className="rounded-lg border border-border-default bg-surface-subtle p-3 text-sm text-content-default whitespace-pre-wrap">
                  {descriptionDiff.oldValue.trim() || <span className="text-content-subtle">Empty</span>}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Updated</p>
                <div className="rounded-lg border border-border-default bg-surface-subtle p-3 text-sm text-content-default whitespace-pre-wrap">
                  {descriptionDiff.newValue.trim() || <span className="text-content-subtle">Empty</span>}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
